import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';

/* ---- Dialog slide-up / fade-out animations ---- */

const slideUp = keyframes({
  from: { opacity: 0, transform: 'translateY(24px)' },
  to: { opacity: 1, transform: 'translateY(0)' },
});

const slideDown = keyframes({
  from: { opacity: 1, transform: 'translateY(0)' },
  to: { opacity: 0, transform: 'translateY(12px)' },
});

export const TILE_DIALOG_ENTER_MS = 220;

export const TileDialogPaper = {
  sx: {
    borderRadius: '16px',
    overflow: 'visible',
    maxWidth: 460,
    width: '100%',
    animation: `${slideUp} ${TILE_DIALOG_ENTER_MS}ms cubic-bezier(0.16, 1, 0.3, 1) both`,
    '&.MuiDialog-paperScrollBody': {
      maxWidth: 460,
    },
  },
};

export const TileDialogBackdrop = {
  sx: {
    backgroundColor: 'rgba(15, 23, 42, 0.35)',
    backdropFilter: 'blur(4px)',
  },
};

export const DialogHeader = styled.div(({ theme }) => ({
  padding: '1.5rem 1.5rem 0',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.25rem',
}));

export const DialogTitle = styled.h2(({ theme }) => ({
  margin: 0,
  fontSize: '1.15rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  letterSpacing: '-0.01em',
  lineHeight: 1.3,
}));

export const DialogSubtitle = styled.p(({ theme }) => ({
  margin: 0,
  fontSize: '0.8rem',
  color: theme.palette.text.secondary,
  lineHeight: 1.5,
}));

export const DialogBody = styled.div({
  padding: '1.25rem 1.5rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '1.25rem',
});

export const DialogFooter = styled.div(({ theme }) => ({
  padding: '0 1.5rem 1.5rem',
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '0.75rem',
}));

/* ---- Config preview chips ---- */

const chipFadeIn = keyframes({
  from: { opacity: 0, transform: 'translateY(6px)' },
  to: { opacity: 1, transform: 'translateY(0)' },
});

export const ConfigPreviewGrid = styled.div({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '0.5rem',
});

export const ConfigChip = styled.div<{ delay?: number }>(({ theme, delay = 0 }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.15rem',
  padding: '0.6rem 0.75rem',
  borderRadius: '10px',
  background: `${theme.palette.primary.main}08`,
  border: `1px solid ${theme.palette.divider}`,
  animation: `${chipFadeIn} 280ms cubic-bezier(0.16, 1, 0.3, 1) both`,
  animationDelay: `${delay}ms`,
}));

export const ConfigChipLabel = styled.span(({ theme }) => ({
  fontSize: '0.65rem',
  fontWeight: 600,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.06em',
  color: theme.palette.text.disabled,
}));

export const ConfigChipValue = styled.span(({ theme }) => ({
  fontSize: '0.82rem',
  fontWeight: 500,
  color: theme.palette.text.primary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

/* ---- Tile editing banner ---- */

export const TileEditingBanner = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0.75rem',
  padding: '0.5rem 0.75rem',
  borderRadius: '10px',
  background: `${theme.palette.primary.main}0A`,
  border: `1px solid ${theme.palette.primary.main}30`,
  fontSize: '0.82rem',
  color: theme.palette.text.secondary,
  flexWrap: 'wrap' as const,
}));

export const TileEditingTitle = styled.span(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.primary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  minWidth: 0,
  flex: '0 1 auto',
}));

export const TileEditingActions = styled.div({
  marginLeft: 'auto',
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
});

/* ---- Sortable wrapper for overview tiles ---- */

export const SortableTileWrapper = styled.div<{ isDragging?: boolean; compact?: boolean }>(({ isDragging, compact }) => ({
  position: 'relative' as const,
  transition: 'box-shadow 200ms ease, opacity 200ms ease',
  opacity: isDragging ? 0.5 : 1,
  touchAction: 'none',
  gridColumn: compact ? 'span 1' : 'span 2',
  gridRow: compact ? 'span 1' : 'span 2',
}));
